<template>
  <section id="payment-methods" :class="[variantStyles.section, sectionClass]" aria-label="Accepted payment methods">
    <div :class="[variantStyles.wrapper, wrapperClass]">
      <h3 v-if="variant !== 'footer'" :class="[variantStyles.title, titleClass]">
        <slot name="title">Pay Your Way</slot>
      </h3>

      <div :class="[variantStyles.list, listClass]">
        <div v-for="p in providers" :key="p.name" :class="[variantStyles.item, itemClass]">
          <img :src="p.src" :alt="p.alt" role="img" />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import type { PropType } from "vue";

// -- static data & types at module scope
interface Provider {
  name: string;
  src: string;
  alt: string;
}
const defaultProviders: Provider[] = [
  { name: "iDEAL", src: "/images/payment-providers/ideal.svg", alt: "iDEAL" },
  { name: "Visa", src: "/images/payment-providers/visa.svg", alt: "Visa" },
  { name: "Mastercard", src: "/images/payment-providers/mastercard.svg", alt: "Mastercard" },
  { name: "AmericanExpress", src: "/images/payment-providers/american-express.svg", alt: "American Express" },
  { name: "PayPal", src: "/images/payment-providers/paypal.svg", alt: "PayPal" },
  { name: "ApplePay", src: "/images/payment-providers/apple-pay.svg", alt: "Apple Pay" },
  { name: "Bancontact", src: "/images/payment-providers/bancontact.svg", alt: "Bancontact" },
  { name: "SEPA", src: "/images/payment-providers/sepa.svg", alt: "SEPA Overboeking / SEPA Incasso" },
  { name: "Klarna", src: "/images/payment-providers/klarna.svg", alt: "Klarna (Betaal later)" },
  { name: "Trustly", src: "/images/payment-providers/Trustly.svg", alt: "Trustly" },
  { name: "Billie", src: "/images/payment-providers/Billie.svg", alt: "Billie (B2B Buy Now Pay Later)" },
];

type Variant = "default" | "footer" | "strip";
const styleMap: Record<Variant, Record<string, string>> = {
  default: {
    section: "border-t border-gray-200 bg-gray-50 py-8 shadow-inner-top",
    wrapper: "max-w-2xl mx-auto px-4 text-center space-y-4",
    title: "text-sm font-semibold text-primary uppercase tracking-wide",
    list: "flex flex-wrap justify-center items-center gap-6",
    item: "w-16 h-16 p-1 flex items-center justify-center",
  },
  footer: {
    // Neutral light footer
    section: "bg-gray-100 py-6 mt-1",
    wrapper: "max-w-6xl mx-auto px-6 flex flex-wrap justify-center items-center space-x-6",
    title: "text-xs font-medium text-gray-700 uppercase tracking-wide",
    list: "flex flex-wrap justify-center items-center gap-4",
    item: "w-10 h-10 flex items-center justify-center",
  },

  strip: {
    section: "fixed bottom-0 inset-x-0 bg-white shadow-lg",
    wrapper: "max-w-4xl mx-auto px-4 py-2 flex justify-between",
    title: "sr-only",
    list: "flex space-x-3",
    item: "w-8 h-8",
  },
};

export default defineComponent({
  name: "AcceptedPaymentsSection",
  props: {
    providers: {
      type: Array as PropType<Provider[]>,
      default: () => defaultProviders,
    },
    variant: {
      type: String as PropType<Variant>,
      default: "default",
    },
    sectionClass: { type: String, default: "" },
    wrapperClass: { type: String, default: "" },
    titleClass: { type: String, default: "" },
    listClass: { type: String, default: "" },
    itemClass: { type: String, default: "" },
  },
  setup(props) {
    const variantStyles = computed(() => {
      return styleMap[props.variant] || styleMap.default;
    });
    return { variantStyles };
  },
});
</script>

<style scoped>
/* subtle top-inner shadow */
.shadow-inner-top {
  box-shadow: inset 0 4px 4px -4px rgba(0, 0, 0, 0.1);
}
/* hover pop */
#payment-methods img {
  transition: transform 0.2s ease-in-out;
}
#payment-methods img:hover {
  transform: scale(1.15);
}
</style>
